import { useEffect, useMemo, useState } from "react";
import { Input, Modal, Select, message } from "antd";

import { MediaGroup } from "../../../api/mediaGroupAPI";

const UNGROUP_VALUE = "__ungroup__";
const NEW_GROUP_VALUE = "__new__";

export interface GroupAssignResult {
  /** 배정할 그룹. null 이면 그룹 해제 */
  targetGroupId: number | null;
  /** 새 그룹으로 만들 때의 이름 (targetGroupId 는 null 로 온다) */
  newGroupName?: string;
}

interface Props {
  open: boolean;
  groups: MediaGroup[];
  /** 배정 대상 개수 (문구 표시용) */
  selectedCount: number;
  itemLabel: string;
  /**
   * 그룹 해제 옵션 노출 여부.
   * 단건 배정(우클릭 → 그룹 지정)에서는 false — 해제는 컨텍스트 메뉴의 "그룹 해제"로 통일한다.
   * 다중 선택 배정에서는 컨텍스트 메뉴가 없으므로 true 를 유지한다.
   */
  allowUngroup?: boolean;
  /**
   * 단건 배정일 때 그 미디어가 현재 속한 그룹.
   * 같은 그룹을 고르면 바뀌는 게 없으므로 적용 버튼을 막는다. (다중 선택에서는 넘기지 않음)
   */
  currentGroupId?: number | null;
  onCancel: () => void;
  onSubmit: (result: GroupAssignResult) => Promise<void>;
}

/**
 * 미디어를 그룹에 배정하는 모달.
 * 기존 그룹을 고르거나, 새 그룹을 만들면서 한 번에 배정할 수 있다.
 */
const MediaGroupAssignModal = ({
  open,
  groups,
  selectedCount,
  itemLabel,
  allowUngroup = true,
  currentGroupId,
  onCancel,
  onSubmit,
}: Props) => {
  const [value, setValue] = useState<string | number>(NEW_GROUP_VALUE);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // 이미 속한 그룹을 기본값으로 잡으면 곧바로 비활성 상태가 되므로, 다른 그룹을 먼저 고른다.
    // 고를 그룹이 없으면 새 그룹 생성 흐름으로 연다.
    const firstOther = groups.find((g) => g.mediaGroupId !== currentGroupId);
    setValue(firstOther ? firstOther.mediaGroupId : NEW_GROUP_VALUE);
    setNewName("");
  }, [open, groups, currentGroupId]);

  const options = useMemo(
    () => [
      ...groups.map((g) => ({
        label: g.mediaGroupId === currentGroupId ? `${g.name} (현재 그룹)` : g.name,
        value: g.mediaGroupId,
      })),
      { label: "＋ 새 그룹 만들기", value: NEW_GROUP_VALUE },
      ...(allowUngroup ? [{ label: "그룹 해제 (그룹없음으로)", value: UNGROUP_VALUE }] : []),
    ],
    [groups, currentGroupId, allowUngroup]
  );

  // 현재 속한 그룹을 그대로 고른 경우 → 변경할 것이 없다
  const isSameGroup =
    currentGroupId != null && value !== NEW_GROUP_VALUE && value !== UNGROUP_VALUE
      ? Number(value) === currentGroupId
      : false;

  const handleOk = async () => {
    if (isSameGroup) return;
    if (value === NEW_GROUP_VALUE && !newName.trim()) {
      message.warning("새 그룹 이름을 입력해주세요.");
      return;
    }
    setSaving(true);
    try {
      await onSubmit(
        value === NEW_GROUP_VALUE
          ? { targetGroupId: null, newGroupName: newName.trim() }
          : { targetGroupId: value === UNGROUP_VALUE ? null : Number(value) }
      );
    } catch (e: any) {
      message.error(e?.message || "그룹 배정에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title="그룹 지정"
      okText="적용"
      cancelText="취소"
      okButtonProps={{ disabled: isSameGroup }}
      confirmLoading={saving}
      onOk={handleOk}
      onCancel={onCancel}
      centered
      destroyOnClose
      width={420}
    >
      <Desc>
        선택한 {itemLabel} <b>{selectedCount}</b>개를 아래 그룹으로 옮깁니다.
        {" "}이미 다른 그룹에 속해 있으면 그 그룹에서 빠집니다.
      </Desc>
      <Select
        style={{ width: "100%" }}
        value={value}
        onChange={(v) => setValue(v)}
        options={options}
      />
      {isSameGroup && <Hint>이미 현재 속한 그룹입니다.</Hint>}
      {value === NEW_GROUP_VALUE && (
        <Input
          style={{ marginTop: 10 }}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onPressEnter={handleOk}
          placeholder="새 그룹 이름"
          maxLength={255}
          autoFocus
        />
      )}
    </Modal>
  );
};

const Desc = ({ children }: { children: React.ReactNode }) => (
  <div style={{ marginBottom: 12, fontSize: 13, color: "#595959", lineHeight: 1.6 }}>{children}</div>
);

const Hint = ({ children }: { children: React.ReactNode }) => (
  <div style={{ marginTop: 8, fontSize: 12.5, color: "#ad6800" }}>{children}</div>
);

export default MediaGroupAssignModal;
